// The Atelier's right column: diff stat, TESTER / APPLIQUER ET DÉPLOYER / ABANDONNER, the test
// result (tail of the output, ok / failed), the applied / pushed / deployed record, the list of
// changed files and a unified diff viewer (added / removed lines coloured, per-file collapse,
// big files collapsed by default and rendered only when opened).
import { h, button, iconButton, badge, emptyState, spinner, icon } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { relativeTime, formatDateTime } from '../../format.js';
import { sessionActions, testTail, formatStat } from './model.js';
import { mergeFileLists, splitPath, diffTotals } from './diff.js';
import './strings.js';

/** Files with more diff lines than this start collapsed. */
const BIG_FILE_LINES = 400;
/** Past this many rendered lines, the next files start collapsed. */
const OPEN_BUDGET_LINES = 2500;
const TAIL_LINES = 40;
const STATUS_LETTERS = { added: 'A', modified: 'M', deleted: 'D', renamed: 'R', copied: 'C', typechange: 'T', unmerged: 'U' };

/**
 * Which files start expanded: small files, in order, until the line budget is spent.
 * @param {Array<{path: string, parsed: object|null}>} files
 * @returns {Set<string>}
 */
export function initialExpanded(files) {
  const open = new Set();
  let budget = OPEN_BUDGET_LINES;
  for (const f of files || []) {
    const n = f.parsed?.lineCount || 0;
    if (!f.parsed || f.parsed.binary || n > BIG_FILE_LINES || n > budget) continue;
    open.add(f.path);
    budget -= n;
  }
  return open;
}

/**
 * Creates the changes column.
 * @param {object} ctx app context
 * @param {{onTest: Function, onApply: Function, onDiscard: Function, onRefresh: Function}} handlers
 * @returns {{el: HTMLElement, update(state: {session: object|null, status: object|null, diff: object|null}): void}}
 */
export function createChangesPanel(ctx, { onTest, onApply, onDiscard, onRefresh }) {
  let session = null;
  let status = null;
  let diff = null;          // { forId, loading, error, data: {stat, files, patch, truncated}, parsed }
  let renderedDiff = undefined;
  let renderedFor = undefined;
  let expanded = new Set();
  let showFullOutput = false;
  let testsAtShown = null;

  const statEl = h('span', { class: 'atl-changes-stat mono-label' });
  const refreshBtn = iconButton('refresh', t('atl.refresh'), { variant: 'ghost', onClick: () => onRefresh?.() });
  const head = h('header', { class: 'atl-changes-head' },
    h('h3', { class: 'atl-changes-title' }, t('atl.changes.title')), statEl, refreshBtn);
  const testBtn = button(t('atl.action.test'), { variant: 'secondary', icon: 'check', onClick: () => onTest?.() });
  const applyBtn = button(t('atl.action.apply'), { variant: 'primary', icon: 'send', onClick: () => onApply?.() });
  const discardBtn = button(t('atl.action.discard'), { variant: 'ghost', class: 'atl-discard', onClick: () => onDiscard?.() });
  const whyEl = h('p', { class: 'atl-actions-why muted', 'aria-live': 'polite' });
  const actions = h('div', { class: 'atl-actions' }, h('div', { class: 'atl-actions-row' }, testBtn, applyBtn, discardBtn), whyEl);
  const record = h('div', { class: 'atl-record' });
  const tests = h('section', { class: 'atl-tests', 'aria-label': t('atl.tests.title') });
  const files = h('div', { class: 'atl-files' });
  const body = h('div', { class: 'atl-changes-body' }, record, tests, files);
  const el = h('section', { class: 'atl-changes', 'aria-label': t('atl.changes.title') }, head, actions, body);

  // --- actions ----------------------------------------------------------------------------------
  function renderActions() {
    const has = diff?.forId === session?.id && diff?.data ? (diff.data.files || []).length > 0 : null;
    const a = sessionActions(session, { mock: !!status?.mock, hasChanges: session?.diffStat ? Number(session.diffStat.files) > 0 : has });
    actions.hidden = !session;
    testBtn.disabled = !a.test;
    testBtn.title = a.why.test || '';
    applyBtn.disabled = !a.apply;
    applyBtn.title = a.why.apply || '';
    discardBtn.disabled = !a.discard;
    discardBtn.title = a.why.discard || '';
    testBtn.classList.toggle('is-busy', session?.state === 'testing');
    discardBtn.classList.toggle('is-busy', session?.busy === 'discard');
    applyBtn.classList.toggle('is-busy', session?.busy === 'apply');
    const why = session && !['applied', 'discarded'].includes(session.state) ? (a.why.apply || '') : '';
    whyEl.textContent = why || '';
    whyEl.hidden = !why;
    const stat = formatStat(session?.diffStat) || (diff?.forId === session?.id && diff?.data ? formatStat(diff.data.stat) : '');
    statEl.textContent = stat;
    refreshBtn.disabled = !session || session.state === 'discarded';
  }

  // --- applied record -----------------------------------------------------------------------------
  function renderRecord() {
    const s = session;
    if (!s || s.state !== 'applied') { record.replaceChildren(); record.hidden = true; return; }
    const lines = [];
    lines.push(h('p', { class: 'atl-record-line is-ok' }, icon('check', { size: 16 }),
      h('span', {}, t('atl.changes.applied', { when: s.appliedAt ? relativeTime(s.appliedAt) : '', commit: String(s.commit || '').slice(0, 7) || '—' }))));
    if (s.forced) lines.push(h('p', { class: 'atl-record-line is-warn' }, icon('alert', { size: 16 }), h('span', {}, t('atl.changes.forced'))));
    if (s.push) {
      lines.push(s.push.ok
        ? h('p', { class: 'atl-record-line is-ok' }, icon('check', { size: 16 }), h('span', {}, t('atl.changes.pushOk', { remote: s.push.remote || 'origin' })))
        : h('p', { class: 'atl-record-line is-warn' }, icon('alert', { size: 16 }), h('span', {}, t('atl.changes.pushFailed', { error: s.push.error || '—' }))));
    }
    if (s.deploy) {
      lines.push(s.deploy.started
        ? h('p', { class: 'atl-record-line is-ok' }, icon('check', { size: 16 }), h('span', {}, t('atl.changes.deployStarted', { when: s.deploy.at ? relativeTime(s.deploy.at) : '' })))
        : h('p', { class: 'atl-record-line is-warn' }, icon('alert', { size: 16 }), h('span', {}, t('atl.changes.deployFailed', { error: s.deploy.error || '—' }))));
    }
    record.hidden = false;
    record.replaceChildren(...lines);
  }

  // --- tests ----------------------------------------------------------------------------------------
  function renderTests() {
    const s = session;
    if (!s || s.state === 'discarded') { tests.hidden = true; tests.replaceChildren(); return; }
    tests.hidden = false;
    const title = h('h4', { class: 'atl-block-title mono-label' }, t('atl.tests.title'));
    if (s.state === 'testing' || s.busy === 'test') {
      tests.replaceChildren(title, h('div', { class: 'atl-tests-running' }, spinner(t('atl.tests.running')), h('span', {}, t('atl.tests.running'))));
      return;
    }
    const r = s.tests;
    if (!r) {
      tests.replaceChildren(title, h('p', { class: 'atl-tests-none muted' }, t('atl.tests.none')));
      return;
    }
    if (testsAtShown !== r.at) { testsAtShown = r.at; showFullOutput = false; }
    const out = typeof r.output === 'string' ? r.output : '';
    const tail = testTail(out, TAIL_LINES);
    const toggle = tail.truncated ? button(showFullOutput ? t('atl.tests.showTail') : t('atl.tests.showFull'), {
      variant: 'link', size: 'sm', class: 'atl-tests-toggle',
      onClick: () => { showFullOutput = !showFullOutput; renderTests(); tests.querySelector?.('.atl-tests-toggle')?.focus?.(); },
    }) : null;
    const pre = h('pre', { class: ['atl-output', { 'is-failed': !r.ok }], tabIndex: 0 }, out ? (showFullOutput ? out : tail.text) : t('atl.tests.empty'));
    // replaceChildren() would print "null" for a missing part: keep only real nodes.
    tests.replaceChildren(...[title,
      h('p', { class: 'atl-tests-result' },
        badge(r.ok ? t('atl.tests.ok') : t('atl.tests.failed'), r.ok ? 'success' : 'danger'),
        r.at ? h('time', { class: 'muted', datetime: String(r.at), title: formatDateTime(r.at) }, t('atl.tests.at', { when: relativeTime(r.at) })) : null),
      out ? h('p', { class: 'atl-tests-caption mono-label' }, showFullOutput ? t('atl.tests.full') : t('atl.tests.tail', { n: tail.shown })) : null,
      pre,
      toggle].filter(Boolean));
    // The summary (pass/fail counts) is at the end of the output: show the end first.
    const toEnd = () => { pre.scrollTop = pre.scrollHeight; };
    if (typeof globalThis.requestAnimationFrame === 'function') globalThis.requestAnimationFrame(toEnd); else toEnd();
  }

  // --- files & diff ------------------------------------------------------------------------------
  function renderFiles() {
    const s = session;
    if (!s) {
      files.replaceChildren(emptyState({ icon: 'layers', title: t('atl.changes.title'), text: t('atl.changes.pick') }));
      return;
    }
    if (s.state === 'discarded') {
      files.replaceChildren(emptyState({ icon: 'close', title: t('atl.state.discarded'), text: t('atl.changes.discarded') }));
      return;
    }
    const d = diff && diff.forId === s.id ? diff : null;
    if (!d || (d.loading && !d.data)) {
      files.replaceChildren(s.state === 'running' && !d?.data
        ? h('p', { class: 'atl-files-wait muted' }, spinner(t('atl.changes.waiting')), t('atl.changes.waiting'))
        : h('div', { class: 'atl-files-wait' }, spinner(t('atl.loading'))));
      return;
    }
    if (d.error && !d.data) {
      files.replaceChildren(h('p', { class: 'atl-alert is-danger', role: 'alert' }, icon('alert', { size: 16 }),
        h('span', {}, t('atl.changes.loadError', { error: d.error.message || t('common.error') }))));
      return;
    }
    const list = mergeFileLists(d.data.files, d.parsed);
    if (!list.length) {
      files.replaceChildren(emptyState({ icon: 'inbox', title: t('atl.changes.none'), text: s.state === 'running' ? t('atl.changes.waiting') : t('atl.changes.noneHint') }));
      return;
    }
    const sections = new Map();
    const nav = h('ul', { class: 'atl-filelist', 'aria-label': t('atl.changes.list') }, list.map((f) => {
      const { dir, name } = splitPath(f.path);
      const p = f.parsed;
      return h('li', {}, h('button', {
        type: 'button', class: 'atl-filelink', title: f.path,
        onClick: () => {
          if (!expanded.has(f.path)) { expanded.add(f.path); rerenderSection(f); }
          const sec = sections.get(f.path);
          sec?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
          sec?.querySelector?.('.atl-file-toggle')?.focus?.({ preventScroll: true });
        },
      }, statusTag(f.status),
      h('span', { class: 'atl-filelink-path' }, dir ? h('span', { class: 'atl-dir' }, dir) : null, h('span', { class: 'atl-base' }, name)),
      p ? h('span', { class: 'atl-counts mono-label' }, h('span', { class: 'is-add' }, `+${p.additions}`), ' ', h('span', { class: 'is-del' }, `−${p.deletions}`)) : null));
    }));
    const viewer = h('div', { class: 'atl-diff' });
    const rerenderSection = (f) => {
      const old = sections.get(f.path);
      const next = fileSection(f);
      sections.set(f.path, next);
      const parent = old?.parentNode;
      if (parent) { parent.insertBefore(next, old); parent.removeChild(old); }
    };
    for (const f of list) {
      const sec = fileSection(f);
      sections.set(f.path, sec);
      viewer.appendChild(sec);
    }
    const fileSectionToggle = (f) => {
      if (expanded.has(f.path)) expanded.delete(f.path); else expanded.add(f.path);
      rerenderSection(f);
      sections.get(f.path)?.querySelector?.('.atl-file-toggle')?.focus?.({ preventScroll: true });
    };
    function fileSection(f) {
      const open = expanded.has(f.path);
      const p = f.parsed;
      const bodyId = `atl-file-${hashId(f.path)}`;
      const toggle = h('button', {
        type: 'button', class: 'atl-file-toggle', 'aria-expanded': String(open), 'aria-controls': bodyId,
        'aria-label': t(open ? 'atl.changes.collapseAria' : 'atl.changes.expandAria', { path: f.path }),
        onClick: () => fileSectionToggle(f),
      }, icon(open ? 'chevronDown' : 'chevronRight', { size: 14 }), statusTag(f.status),
      h('span', { class: 'atl-file-path' }, f.path),
      p ? h('span', { class: 'atl-counts mono-label' }, h('span', { class: 'is-add' }, `+${p.additions}`), ' ', h('span', { class: 'is-del' }, `−${p.deletions}`)) : null,
      !open && p?.lineCount ? h('span', { class: 'atl-file-lines muted' }, t('atl.changes.lines', { n: p.lineCount })) : null);
      return h('section', { class: ['atl-file', `is-${f.status}`] },
        h('header', { class: 'atl-file-head' }, toggle),
        h('div', { class: 'atl-file-body', id: bodyId, hidden: !open }, open ? fileBody(f) : null));
    }
    files.replaceChildren(...[
      d.data.truncated || d.parsed?.cut ? h('p', { class: 'atl-alert is-amber' }, icon('alert', { size: 16 }), h('span', {}, t('atl.changes.truncated'))) : null,
      h('h4', { class: 'atl-block-title mono-label' }, t('atl.changes.list')),
      nav, viewer].filter(Boolean));
  }

  function fileBody(f) {
    const p = f.parsed;
    if (!p) return h('p', { class: 'atl-file-note muted' }, t('atl.changes.notInPatch'));
    if (p.binary) return h('p', { class: 'atl-file-note muted' }, t('atl.changes.binary'));
    if (!p.hunks.length) return h('p', { class: 'atl-file-note muted' }, t('atl.changes.noContent'));
    const rows = [];
    for (const hunk of p.hunks) {
      rows.push(h('div', { class: 'atl-ln is-hunk' },
        h('span', { class: 'atl-no', 'aria-hidden': 'true' }, '…'), h('span', { class: 'atl-no', 'aria-hidden': 'true' }, '…'),
        h('span', { class: 'atl-code' }, hunk.header)));
      for (const l of hunk.lines) {
        const sign = l.type === 'add' ? '+' : l.type === 'del' ? '-' : l.type === 'note' ? '\\' : ' ';
        rows.push(h('div', { class: ['atl-ln', `is-${l.type}`] },
          h('span', { class: 'atl-no', 'aria-hidden': 'true' }, l.oldNo ?? ''),
          h('span', { class: 'atl-no', 'aria-hidden': 'true' }, l.newNo ?? ''),
          h('span', { class: 'atl-code' }, h('span', { class: 'atl-sign' }, sign), l.text)));
      }
    }
    return h('div', { class: 'atl-lines', role: 'group', 'aria-label': f.path }, rows);
  }

  function statusTag(status) {
    const key = STATUS_LETTERS[status] ? status : 'modified';
    return h('span', { class: ['atl-status', `is-${key}`], title: t(`atl.changes.status.${key}`), 'aria-label': t(`atl.changes.status.${key}`) },
      STATUS_LETTERS[key]);
  }

  function hashId(s) {
    let x = 0;
    for (const c of String(s)) x = (x * 31 + c.codePointAt(0)) >>> 0;
    return x.toString(36);
  }

  return {
    el,
    /**
     * @param {{session: object|null, status: object|null, diff: object|null}} state
     */
    update(state) {
      const sessionChanged = (state.session?.id ?? null) !== (session?.id ?? null);
      session = state.session || null;
      status = state.status || null;
      diff = state.diff || null;
      if (sessionChanged) { showFullOutput = false; testsAtShown = null; }
      renderActions();
      renderRecord();
      renderTests();
      const key = `${session?.id ?? ''}|${session?.state === 'discarded' ? 'x' : ''}|${session?.state === 'running' ? 'r' : ''}`;
      if (diff !== renderedDiff || key !== renderedFor) {
        if (diff !== renderedDiff && diff?.data && diff.forId === session?.id) {
          const list = mergeFileLists(diff.data.files, diff.parsed);
          // Keep what the admin opened when the same session's diff is reloaded.
          expanded = sessionChanged || !renderedDiff?.data || renderedDiff.forId !== diff.forId ? initialExpanded(list) : new Set(expanded);
        } else if (sessionChanged) {
          expanded = new Set();
        }
        renderedDiff = diff;
        renderedFor = key;
        renderFiles();
      }
    },
    /** Totals of the diff shown (for the mobile tab counter). */
    totals: () => (diff?.parsed ? diffTotals(diff.parsed) : null),
  };
}
